import { BOUTS_V2, COTES_DEMI_PLAN_V2, couperDemiDroite, evaluerFonctionV2, familleFonctionV2, fonctionCanoniqueV2, mesureAngle, normaliserRepereV2, } from './figure-v2.js';
import { distance, formaterNombre, homothetie, intersectionCercles, intersectionDroiteCercle, intersectionDroites, milieu, moins, normale, plus, refleter, tourner, tousFinis, translater, unitaire, } from './geometrie.js';
export const VERSION_CONSTRUCTION = 1;
export const OBJETS_MAX_CONSTRUCTION = 300; // = OBJETS_MAX de figure-v2.ts
/* ================================================================ les bornes */
/** Le motif d'un identifiant de l'élève (§ 1.2). */
export const MOTIF_ID_ELEVE = /^[a-z][a-z0-9]{0,11}$/;
/** Nom affiché : 8 caractères au plus (= NOM_MAX de figure-v2.ts). */
export const NOM_MAX_CONSTRUCTION = 8;
/** Texte posé par l'élève : 40 caractères au plus (= TEXTE_MAX de figure-v2.ts). */
export const TEXTE_MAX_CONSTRUCTION = 40;
/** Tout nombre du document tient dans ±1000 (= BORNE_COORDONNEE de figure-v2.ts). */
export const BORNE_CONSTRUCTION = 1000;
/** Les abscisses d'une table de valeurs : 40 au plus (P3). */
export const TABLE_VALEURS_MAX = 40;
/** Une unité (« cm », « m », « km ») : 8 caractères au plus. */
const UNITE_MAX = 8;
/** Le numéro d'un exercice de copie : 40 caractères au plus. */
const EXERCICE_MAX = 40;
/** La phase qui livre chaque définition (§ 1.2). */
export const PHASE_DEFINITION = {
    point_libre: 1,
    point_sur: 2,
    milieu: 2,
    intersection: 2,
    segment: 1,
    droite: 1,
    demi_droite: 2,
    perpendiculaire: 2,
    parallele: 2,
    mediatrice: 2,
    bissectrice: 2,
    cercle_par_point: 1,
    cercle_rayon: 1,
    cercle_report: 2,
    polygone: 1,
    translation: 2,
    rotation: 2,
    reflexion: 2,
    homothetie: 2,
    image: 2,
    angle: 1,
    mesure_longueur: 1,
    mesure_angle: 1,
    texte: 1,
    fonction: 3,
    fonction_par_points: 3,
    vecteur: 3,
    demi_plan: 3,
    table_valeurs: 3,
    esquisse: 3,
};
/** La phase livrée : une définition d'une phase ultérieure est refusée (§ 1.2). */
export const PHASE_LIVREE = 3;
/** La définition est-elle livrée ? */
export function definitionLivree(type) {
    return PHASE_DEFINITION[type] <= PHASE_LIVREE;
}
const estTypeDefinition = (t) => typeof t === 'string' && Object.prototype.hasOwnProperty.call(PHASE_DEFINITION, t);
/* ============================================================ l'énoncé */
/** Un identifiant de l'énoncé commence par « e: » ; aucun identifiant de l'élève ne le peut. */
export const estIdEnonce = (id) => id.startsWith('e:');
/**
 * Les identifiants des objets de l'énoncé, dans l'ordre de `f.objets` (§ 1.1,
 * principe 4) : « e:A » pour le point nommé A, « e:3 » pour l'objet d'indice
 * 3 (base 0) quand il n'est pas un point. Calculés des deux côtés par cette
 * seule fonction ; stables tant que la figure de la banque ne change pas.
 */
export function idsEnonce(f) {
    return f.objets.map((o, i) => (o.type === 'point' ? `e:${o.nom}` : `e:${i}`));
}
/** Les objets de l'énoncé, résolus : ils sont déjà des coordonnées. */
function resoudreEnonce(f) {
    const ids = idsEnonce(f);
    const sortie = [];
    f.objets.forEach((o, i) => {
        const id = ids[i];
        switch (o.type) {
            case 'point':
                sortie.push({ id, nom: o.nom, genre: 'point', p: { x: o.x, y: o.y } });
                break;
            case 'etiquette':
                sortie.push({ id, genre: 'texte', p: { x: o.x, y: o.y }, texte: o.texte });
                break;
            case 'segment':
            case 'droite':
                sortie.push({ id, style: o.style, genre: 'ligne', sorte: o.type, a: o.de, b: o.a });
                break;
            case 'polygone':
                sortie.push({ id, genre: 'polygone', sommets: o.sommets });
                break;
            case 'cercle':
                sortie.push({ id, genre: 'cercle', centre: o.centre, rayon: o.rayon });
                break;
            case 'fonction':
                sortie.push({ id, genre: 'fonction', f: o.fonction, domaine: o.domaine });
                break;
            case 'cote':
                sortie.push({ id, genre: 'marque', sorte: 'cote', de: o.de, a: o.a, texte: o.texte });
                break;
            case 'vecteur':
                sortie.push({ id, genre: 'vecteur', de: o.de, a: o.a });
                break;
            case 'demi_plan':
                sortie.push({
                    id,
                    genre: 'demi_plan',
                    a: o.de,
                    b: o.a,
                    cote: o.cote,
                    stricte: o.stricte,
                });
                break;
            case 'angle':
                sortie.push({
                    id,
                    genre: 'marque',
                    sorte: 'angle',
                    sommet: o.sommet,
                    de: o.de,
                    a: o.a,
                    droit: o.droit,
                    texte: o.texte,
                });
                break;
        }
    });
    return sortie;
}
/**
 * Les objets qu'une définition cite, dans l'ordre de ses champs. Un polygone
 * en cite autant que de sommets (`sommets[0]`, `sommets[1]`…).
 */
export function referencesDe(def) {
    const r = (...roles) => roles.map((role) => ({
        role,
        id: def[role],
    }));
    switch (def.type) {
        case 'point_libre':
        case 'texte':
        case 'fonction':
        case 'esquisse':
            return [];
        case 'point_sur':
            return r('objet');
        case 'milieu':
        case 'intersection':
        case 'segment':
        case 'droite':
        case 'mediatrice':
        case 'translation':
        case 'mesure_longueur':
        case 'vecteur':
            return r('de', 'a');
        case 'demi_droite':
            return r('origine', 'par');
        case 'perpendiculaire':
        case 'parallele':
            return r('a', 'par');
        case 'bissectrice':
        case 'angle':
        case 'mesure_angle':
            return r('sommet', 'de', 'a');
        case 'cercle_par_point':
            return r('centre', 'par');
        case 'cercle_rayon':
        case 'rotation':
        case 'homothetie':
            return r('centre');
        case 'cercle_report':
            return r('centre', 'longueurDe');
        case 'polygone':
            return (Array.isArray(def.sommets) ? def.sommets : []).map((id, i) => ({
                role: `sommets[${i}]`,
                id,
            }));
        case 'reflexion':
            return r('axe');
        case 'image':
            return r('source', 'par');
        case 'fonction_par_points':
            return (Array.isArray(def.points) ? def.points : []).map((id, i) => ({
                role: `points[${i}]`,
                id,
            }));
        case 'demi_plan':
            return r('frontiere');
        case 'table_valeurs':
            return r('fonction');
    }
}
/** Le genre que produit chaque définition ; `image` prend celui de sa source. */
const GENRE_PAR_TYPE = {
    point_libre: 'point',
    point_sur: 'point',
    milieu: 'point',
    intersection: 'point',
    segment: 'ligne',
    droite: 'ligne',
    demi_droite: 'ligne',
    perpendiculaire: 'ligne',
    parallele: 'ligne',
    mediatrice: 'ligne',
    bissectrice: 'ligne',
    cercle_par_point: 'cercle',
    cercle_rayon: 'cercle',
    cercle_report: 'cercle',
    polygone: 'polygone',
    translation: 'transformation',
    rotation: 'transformation',
    reflexion: 'transformation',
    homothetie: 'transformation',
    image: 'image',
    angle: 'marque',
    mesure_longueur: 'mesure',
    mesure_angle: 'mesure',
    texte: 'texte',
    fonction: 'fonction',
    fonction_par_points: 'fonction',
    vecteur: 'vecteur',
    demi_plan: 'demi_plan',
    table_valeurs: 'marque',
    esquisse: 'marque',
};
const POINT = ['point'];
const LIGNE = ['ligne'];
const TRACE = ['ligne', 'cercle', 'polygone', 'fonction'];
/** Le genre qu'un rôle attend ; un rôle absent de la table accepte tout genre. */
function genresAttendus(type, role) {
    if (role.startsWith('sommets[') || role.startsWith('points['))
        return POINT;
    switch (type) {
        case 'point_sur':
            return TRACE;
        case 'intersection':
            return ['ligne', 'cercle'];
        case 'perpendiculaire':
        case 'parallele':
            return role === 'a' ? LIGNE : POINT;
        case 'cercle_report':
            return role === 'longueurDe' ? LIGNE : POINT;
        case 'reflexion':
            return LIGNE;
        case 'image':
            return role === 'par' ? ['transformation'] : ['point', 'ligne', 'cercle', 'polygone'];
        case 'demi_plan':
            return ['ligne', 'fonction'];
        case 'table_valeurs':
            return ['fonction'];
        default:
            return POINT;
    }
}
const LIBELLE_GENRE = {
    point: 'un point',
    ligne: 'une ligne',
    cercle: 'un cercle',
    polygone: 'un polygone',
    transformation: 'une transformation',
    fonction: 'une fonction',
    mesure: 'une mesure',
    marque: 'une marque',
    texte: 'un texte',
    vecteur: 'un vecteur',
    demi_plan: 'un demi-plan',
};
/**
 * L'objet et tous ceux qui en dépendent, transitivement (`supprimer` les
 * retire dans le même geste, § 1.3). Un passage suffit : l'ordre du document
 * est topologique.
 */
export function avecDependants(doc, ids) {
    const touches = new Set(ids);
    for (const o of doc.objets) {
        if (touches.has(o.id))
            continue;
        if (referencesDe(o.def).some((r) => touches.has(r.id)))
            touches.add(o.id);
    }
    return touches;
}
/* ============================================================= la résolution */
/** La fenêtre de secours d'un document dont le repère est illisible (déjà un grief). */
const REPERE_SECOURS = {
    xMin: -10,
    xMax: 10,
    yMin: -10,
    yMax: 10,
    axes: false,
    grille: false,
    pas: null,
};
const estObjet = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
/** Le seuil sous lequel deux points sont confondus. */
const CONFONDUS = 1e-9;
function entete(o, id) {
    return {
        id,
        ...(typeof o.nom === 'string' && o.nom.trim() !== '' ? { nom: o.nom.trim() } : {}),
        ...(o.style === 'plein' || o.style === 'pointille' ? { style: o.style } : {}),
        ...(o.masque === true ? { masque: true } : {}),
    };
}
/** Tous les nombres d'un objet résolu sont-ils finis ? Garde-fou : `NaN` ne sort jamais. */
function corpsFini(c) {
    const pts = (...ps) => ps.every((p) => tousFinis(p.x, p.y));
    switch (c.genre) {
        case 'point':
        case 'texte':
            return pts(c.p);
        case 'ligne':
            return pts(c.a, c.b);
        case 'cercle':
            return pts(c.centre) && tousFinis(c.rayon);
        case 'polygone':
            return pts(...c.sommets);
        case 'mesure':
            return tousFinis(c.valeur) && pts(c.ou, ...c.points);
        case 'marque':
            return c.sorte === 'angle'
                ? pts(c.sommet, c.de, c.a)
                : c.sorte === 'cote'
                    ? pts(c.de, c.a)
                    : c.sorte === 'esquisse'
                        ? c.traits.every((t) => pts(...t))
                        : pts(...c.points);
        case 'vecteur':
            return pts(c.de, c.a);
        case 'demi_plan':
            return pts(c.a, c.b);
        default:
            return true;
    }
}
/** Un point sans valeur : `corpsFini` le rattrape et rend l'objet indéfini. */
const NULLE = { x: Number.NaN, y: Number.NaN };
/**
 * Le point d'un tracé au paramètre `t` — l'abscisse curviligne normalisée de
 * `point_sur` (§ 1.2), avec la MÊME convention que `projeterSurObjet` de
 * `aimanter.ts` : ligne a + t·(b − a), cercle en tours depuis l'axe des x,
 * polygone en fraction du périmètre.
 *
 * Ce calcul est écrit ici, et non repris de `aimanter.ts`, parce que
 * `aimanter.ts` importe déjà `construction.ts` : lui emprunter une fonction
 * fermerait un cycle entre les deux modules. La courbe d'une fonction est de
 * la phase 3 — elle demande la fenêtre, que la résolution d'un objet ne
 * reçoit pas — et rend `null`.
 */
function pointSurTrace(o, t) {
    if (!Number.isFinite(t))
        return null;
    switch (o.genre) {
        case 'ligne': {
            /* Le paramètre est borné comme la ligne : [0, 1] pour un segment, t ≥ 0
             * pour une demi-droite, libre pour une droite. */
            const u = o.sorte === 'segment'
                ? Math.min(1, Math.max(0, t))
                : o.sorte === 'demi_droite'
                    ? Math.max(0, t)
                    : t;
            return { x: o.a.x + (o.b.x - o.a.x) * u, y: o.a.y + (o.b.y - o.a.y) * u };
        }
        case 'cercle': {
            const a = 2 * Math.PI * t;
            return { x: o.centre.x + o.rayon * Math.cos(a), y: o.centre.y + o.rayon * Math.sin(a) };
        }
        case 'polygone': {
            const n = o.sommets.length;
            if (n < 3)
                return null;
            const cotes = o.sommets.map((s, i) => distance(s, o.sommets[(i + 1) % n]));
            const perimetre = cotes.reduce((s, l) => s + l, 0);
            if (!(perimetre > CONFONDUS))
                return null;
            let reste = (((t % 1) + 1) % 1) * perimetre;
            for (let i = 0; i < n; i += 1) {
                const l = cotes[i];
                if (reste <= l || i === n - 1) {
                    const a = o.sommets[i];
                    const b = o.sommets[(i + 1) % n];
                    const u = l > CONFONDUS ? Math.min(1, reste / l) : 0;
                    return { x: a.x + (b.x - a.x) * u, y: a.y + (b.y - a.y) * u };
                }
                reste -= l;
            }
            return null;
        }
        default:
            return null;
    }
}
/**
 * L'intersection de deux objets tracés, sur leurs SUPPORTS (la droite qui
 * porte un segment, le cercle entier) et non sur le morceau dessiné.
 *
 * C'est un choix : un élève qui fait glisser un point voit l'intersection
 * suivre au lieu de disparaître dès qu'elle sort du segment. Une définition
 * qui s'évanouit au milieu d'un geste rendrait tout ce qui en dépend
 * indéfini, et le dessin clignoterait (§ 1.4, une seule voie de calcul).
 *
 * `branche` range les deux solutions dans l'ordre stable de `geometrie.ts`
 * (plus petit x, puis plus petit y) ; deux droites n'en ont qu'une.
 */
function intersectionDe(m, n, branche) {
    const i = branche === 0 ? 0 : 1;
    if (m.genre === 'ligne' && n.genre === 'ligne') {
        return intersectionDroites(m.a, m.b, n.a, n.b);
    }
    if (m.genre === 'ligne' && n.genre === 'cercle') {
        return intersectionDroiteCercle(m.a, m.b, n.centre, n.rayon)?.[i] ?? null;
    }
    if (m.genre === 'cercle' && n.genre === 'ligne') {
        return intersectionDroiteCercle(n.a, n.b, m.centre, m.rayon)?.[i] ?? null;
    }
    if (m.genre === 'cercle' && n.genre === 'cercle') {
        return intersectionCercles(m.centre, m.rayon, n.centre, n.rayon)?.[i] ?? null;
    }
    return null;
}
/**
 * L'image d'un objet par une transformation (§ 1.2, `image`). Le rayon d'un
 * cercle se mesure sur l'image d'un point de son bord : la transformation est
 * opaque (une fonction), et c'est la seule façon de suivre une homothétie
 * sans savoir laquelle c'est.
 */
function imageDe(src, f) {
    switch (src.genre) {
        case 'point':
            return { genre: 'point', p: f(src.p) };
        case 'ligne':
            return { genre: 'ligne', sorte: src.sorte, a: f(src.a), b: f(src.b) };
        case 'polygone':
            return { genre: 'polygone', sommets: src.sommets.map(f) };
        case 'cercle': {
            const centre = f(src.centre);
            const bord = f({ x: src.centre.x + src.rayon, y: src.centre.y });
            return { genre: 'cercle', centre, rayon: distance(centre, bord) };
        }
        default:
            return { raison: "cet objet n'a pas d'image" };
    }
}
/**
 * Le corps résolu d'une définition LIVRÉE dont toutes les références sont
 * résolues et du bon genre — ou la raison pour laquelle elle n'a pas de
 * solution à cet instant.
 */
function calculer(def, lire) {
    const point = (id) => {
        const o = lire(id);
        return o.genre === 'point' ? o.p : { x: Number.NaN, y: Number.NaN };
    };
    switch (def.type) {
        case 'point_libre':
            return { genre: 'point', p: { x: def.x, y: def.y } };
        case 'segment':
        case 'droite': {
            const a = point(def.de);
            const b = point(def.a);
            if (distance(a, b) < CONFONDUS)
                return { raison: 'ses deux points sont confondus' };
            return { genre: 'ligne', sorte: def.type, a, b };
        }
        case 'cercle_par_point': {
            const centre = point(def.centre);
            const rayon = distance(centre, point(def.par));
            if (!(rayon >= CONFONDUS))
                return { raison: 'le point est confondu avec le centre' };
            return { genre: 'cercle', centre, rayon };
        }
        case 'cercle_rayon': {
            if (!(def.rayon > 0) || !Number.isFinite(def.rayon)) {
                return { raison: 'rayon non positif' };
            }
            return { genre: 'cercle', centre: point(def.centre), rayon: def.rayon };
        }
        case 'polygone': {
            if (def.sommets.length < 3)
                return { raison: 'moins de trois sommets' };
            return { genre: 'polygone', sommets: def.sommets.map(point) };
        }
        case 'angle':
        case 'mesure_angle': {
            const sommet = point(def.sommet);
            const de = point(def.de);
            const a = point(def.a);
            if (distance(sommet, de) < CONFONDUS || distance(sommet, a) < CONFONDUS) {
                return { raison: 'un côté part du sommet sans longueur' };
            }
            if (def.type === 'angle') {
                return {
                    genre: 'marque',
                    sorte: 'angle',
                    sommet,
                    de,
                    a,
                    droit: def.droit === true,
                    texte: null,
                };
            }
            return {
                genre: 'mesure',
                sorte: 'angle',
                valeur: mesureAngle(sommet, de, a),
                ou: sommet,
                points: [sommet, de, a],
            };
        }
        case 'mesure_longueur': {
            const a = point(def.de);
            const b = point(def.a);
            return {
                genre: 'mesure',
                sorte: 'longueur',
                valeur: distance(a, b),
                ou: milieu(a, b),
                points: [a, b],
            };
        }
        case 'texte':
            return { genre: 'texte', p: { x: def.x, y: def.y }, texte: def.texte };
        case 'esquisse':
            return { genre: 'marque', sorte: 'esquisse', traits: def.traits };
        /* ─────────────────────────── P2 : les points ─────────────────────────── */
        case 'point_sur': {
            const p = pointSurTrace(lire(def.objet), def.t);
            if (p === null)
                return { raison: "l'objet n'a pas de tracé où poser un point" };
            return { genre: 'point', p };
        }
        case 'milieu':
            return { genre: 'point', p: milieu(point(def.de), point(def.a)) };
        case 'intersection': {
            const p = intersectionDe(lire(def.de), lire(def.a), def.branche);
            if (p === null)
                return { raison: 'les deux objets ne se coupent pas' };
            return { genre: 'point', p };
        }
        /* ─────────────────────────── P2 : les lignes ─────────────────────────── */
        case 'demi_droite': {
            const a = point(def.origine);
            const b = point(def.par);
            if (distance(a, b) < CONFONDUS)
                return { raison: 'ses deux points sont confondus' };
            return { genre: 'ligne', sorte: 'demi_droite', a, b };
        }
        case 'perpendiculaire':
        case 'parallele': {
            const l = lire(def.a);
            if (l.genre !== 'ligne')
                return { raison: "la référence n'est pas une ligne" };
            const u = unitaire(moins(l.b, l.a));
            if (u === null)
                return { raison: 'la ligne portée est dégénérée' };
            const par = point(def.par);
            /* La perpendiculaire tourne la direction d'un quart de tour ; la
             * parallèle la garde. Toutes deux sont des droites entières. */
            const d = def.type === 'perpendiculaire' ? normale(u) : u;
            return { genre: 'ligne', sorte: 'droite', a: par, b: plus(par, d) };
        }
        case 'mediatrice': {
            const a = point(def.de);
            const b = point(def.a);
            const u = unitaire(moins(b, a));
            if (u === null)
                return { raison: 'ses deux points sont confondus' };
            const m = milieu(a, b);
            return { genre: 'ligne', sorte: 'droite', a: m, b: plus(m, normale(u)) };
        }
        case 'bissectrice': {
            const s = point(def.sommet);
            const u = unitaire(moins(point(def.de), s));
            const v = unitaire(moins(point(def.a), s));
            if (u === null || v === null)
                return { raison: 'un côté part du sommet sans longueur' };
            /* La somme de deux vecteurs unitaires pointe DANS l'angle : c'est la
             * bissectrice intérieure. Nulle, les deux côtés sont opposés (angle
             * plat) et la bissectrice n'est plus déterminée. */
            const w = unitaire(plus(u, v));
            if (w === null)
                return { raison: 'les deux côtés sont opposés' };
            /* Une bissectrice part du sommet : c'est une demi-droite, pas une droite. */
            return { genre: 'ligne', sorte: 'demi_droite', a: s, b: plus(s, w) };
        }
        /* ─────────────────────────── P2 : le compas ──────────────────────────── */
        case 'cercle_report': {
            const l = lire(def.longueurDe);
            if (l.genre !== 'ligne')
                return { raison: "la référence n'est pas une ligne" };
            const rayon = distance(l.a, l.b);
            if (!(rayon >= CONFONDUS))
                return { raison: 'la longueur reportée est nulle' };
            return { genre: 'cercle', centre: point(def.centre), rayon };
        }
        /* ─────────────────────── P2 : les transformations ────────────────────── */
        /* Une transformation n'a pas de tracé : `corpsFini` ne peut rien vérifier
         * sur une fonction. Ses données sont donc contrôlées ICI, avant de la
         * rendre — sans quoi un `NaN` entrerait dans l'image sans être vu. */
        case 'translation': {
            const u = moins(point(def.a), point(def.de));
            if (!tousFinis(u.x, u.y))
                return { raison: 'le vecteur est sans valeur' };
            return { genre: 'transformation', appliquer: (p) => translater(p, u) };
        }
        case 'rotation': {
            const c = point(def.centre);
            if (!tousFinis(c.x, c.y, def.angle))
                return { raison: 'le centre ou l’angle est sans valeur' };
            return { genre: 'transformation', appliquer: (p) => tourner(p, c, def.angle) };
        }
        case 'reflexion': {
            const l = lire(def.axe);
            if (l.genre !== 'ligne')
                return { raison: "l'axe n'est pas une ligne" };
            const { a, b } = l;
            if (unitaire(moins(b, a)) === null)
                return { raison: "l'axe est dégénéré" };
            return { genre: 'transformation', appliquer: (p) => refleter(p, a, b) ?? NULLE };
        }
        case 'homothetie': {
            const c = point(def.centre);
            if (!tousFinis(c.x, c.y, def.rapport))
                return { raison: 'le centre ou le rapport est sans valeur' };
            if (def.rapport === 0)
                return { raison: 'le rapport est nul' };
            return { genre: 'transformation', appliquer: (p) => homothetie(p, c, def.rapport) };
        }
        case 'image': {
            const tr = lire(def.par);
            if (tr.genre !== 'transformation')
                return { raison: "la référence n'est pas une transformation" };
            return imageDe(lire(def.source), tr.appliquer);
        }
        /* ─────────────────── P3 : le repère et les fonctions ─────────────────── */
        case 'fonction': {
            /* La MÊME conversion que la figure d'un défi (`fonctionCanoniqueV2`) :
             * la courbe d'une construction et celle d'un énoncé qui portent les
             * mêmes paramètres doivent être la même courbe, sans quoi l'élève
             * pourrait être jugé faux contre sa propre consigne. */
            const r = fonctionCanoniqueV2(def.famille, def.parametres, def.domaine);
            if ('griefs' in r)
                return { raison: r.griefs[0] ?? 'paramètres illisibles' };
            const bouts = r.domaine !== null ? boutsLus(def.bouts) : null;
            return {
                genre: 'fonction',
                f: r.fonction,
                domaine: r.domaine,
                ...(bouts === null ? {} : { bouts }),
            };
        }
        case 'fonction_par_points': {
            const a = point(def.points[0]);
            const b = point(def.points[1]);
            /* Deux points de même abscisse portent une VERTICALE : c'est une droite,
             * jamais le graphique d'une fonction. L'outil « droite » la trace ; la
             * courbe, elle, reste sans solution — et le dit. */
            if (Math.abs(b.x - a.x) < CONFONDUS) {
                return { raison: 'ses deux points ont la même abscisse' };
            }
            const pente = (b.y - a.y) / (b.x - a.x);
            return {
                genre: 'fonction',
                f: { famille: 'lineaire', a: pente, b: a.y - pente * a.x },
                domaine: null,
            };
        }
        case 'vecteur': {
            const de = point(def.de);
            const a = point(def.a);
            if (distance(de, a) < CONFONDUS)
                return { raison: 'ses deux points sont confondus' };
            return { genre: 'vecteur', de, a };
        }
        case 'demi_plan': {
            const f = lire(def.frontiere);
            let a;
            let b;
            if (f.genre === 'ligne') {
                a = f.a;
                b = f.b;
            }
            else if (f.genre === 'fonction' && f.f.famille === 'lineaire') {
                /* Une frontière COURBE ne borne pas un demi-plan de ce programme : au
                 * 2e cycle, les inéquations à deux variables (systèmes, optimisation)
                 * sont du premier degré. On prend deux points de la droite. */
                a = { x: 0, y: f.f.b };
                b = { x: 1, y: f.f.a + f.f.b };
            }
            else
                return { raison: "la frontière n'est pas une droite" };
            if (distance(a, b) < CONFONDUS)
                return { raison: 'la frontière est dégénérée' };
            const vertical = Math.abs(a.x - b.x) < CONFONDUS;
            const horizontal = Math.abs(a.y - b.y) < CONFONDUS;
            if ((vertical && (def.cote === 'dessus' || def.cote === 'dessous')) ||
                (horizontal && (def.cote === 'gauche' || def.cote === 'droite'))) {
                return { raison: `le côté « ${def.cote} » ne dit rien d'une telle frontière` };
            }
            return { genre: 'demi_plan', a, b, cote: def.cote, stricte: def.stricte === true };
        }
        case 'table_valeurs': {
            const f = lire(def.fonction);
            if (f.genre !== 'fonction')
                return { raison: "la référence n'est pas une fonction" };
            /* Une abscisse hors du domaine, ou dont l'image n'existe pas (la racine
             * d'un négatif), ne pose pas de point : elle est ignorée, elle ne rend
             * pas la table entière indéfinie. */
            const points = [];
            for (const x of def.x) {
                if (!Number.isFinite(x))
                    continue;
                if (f.domaine !== null && (x < f.domaine[0] || x > f.domaine[1]))
                    continue;
                const y = evaluerFonctionV2(f.f, x);
                if (Number.isFinite(y))
                    points.push({ x, y });
            }
            if (points.length === 0)
                return { raison: 'aucune abscisse de la table ne donne un point' };
            return { genre: 'marque', sorte: 'table', points };
        }
        default: {
            /* Toutes les définitions du § 1.2 sont traitées : le typeur voit ici un
             * `never`, et c'est la preuve qu'aucune n'a été oubliée. La branche
             * reste pour qu'une définition ajoutée demain ne tombe pas dans le vide
             * — elle cesserait de compiler si on la retirait. */
            const jamais = def;
            return { raison: `« ${jamais.type} » n'est pas livré` };
        }
    }
}
/**
 * Résout le document : l'énoncé (« e:… ») puis les objets de l'élève, dans
 * l'ordre du tableau, en un seul passage (§ 1.4). Refait tout à chaque appel :
 * une seule voie de calcul, pour l'atelier comme pour la correction.
 *
 * `enonce` est la figure de l'énoncé NORMALISÉE (`normaliserFigureV2`), ou
 * `null` (page vierge). Un énoncé dans l'espace n'apporte aucun objet au plan.
 */
export function resoudreConstruction(doc, enonce, options = {}) {
    const phase = options.phase ?? PHASE_LIVREE;
    const griefs = [];
    const objets = new Map();
    /** Le genre de chaque objet, même indéfini : c'est lui qu'on contrôle. */
    const genres = new Map();
    const plan = enonce !== null && enonce.espace === 'plan' ? enonce : null;
    for (const o of plan === null ? [] : resoudreEnonce(plan)) {
        objets.set(o.id, o);
        if (o.genre !== 'indefini')
            genres.set(o.id, o.genre);
    }
    const lu = normaliserRepereV2(estObjet(doc) ? doc.repere : undefined);
    let repere;
    if ('repere' in lu)
        repere = lu.repere;
    else {
        griefs.push(...lu.griefs.map((g) => `document, ${g}`));
        repere = plan?.repere ?? REPERE_SECOURS;
    }
    const unite = estObjet(doc) && typeof doc.unite === 'string' ? doc.unite : null;
    const bruts = estObjet(doc) && Array.isArray(doc.objets) ? doc.objets : [];
    /* La première place de chaque identifiant : pour dire « défini après lui ». */
    const places = new Map();
    bruts.forEach((o, i) => {
        if (estObjet(o) && typeof o.id === 'string' && !places.has(o.id))
            places.set(o.id, i);
    });
    bruts.forEach((o, rang) => {
        const quoi = `objet ${rang + 1}`;
        if (!estObjet(o) || typeof o.id !== 'string') {
            griefs.push(`${quoi} : identifiant absent`);
            return;
        }
        const id = o.id;
        if (!MOTIF_ID_ELEVE.test(id)) {
            griefs.push(`${quoi} : identifiant « ${id} » hors du motif (« p1 », « d3 »)`);
            return;
        }
        if (objets.has(id) || places.get(id) !== rang) {
            griefs.push(`${quoi} : identifiant « ${id} » déjà pris`);
            return;
        }
        const tete = entete(o, id);
        const indefini = (raison) => {
            objets.set(id, { ...tete, genre: 'indefini', raison });
        };
        const def = o.def;
        if (!estObjet(def) || !estTypeDefinition(def.type)) {
            griefs.push(`${quoi} (${id}) : définition « ${String(estObjet(def) ? def.type : def)} » inconnue`);
            indefini('définition inconnue');
            return;
        }
        const d = def;
        if (PHASE_DEFINITION[d.type] > phase) {
            griefs.push(`${quoi} (${id}) : « ${d.type} » est réservé à la phase ${PHASE_DEFINITION[d.type]}`);
            indefini(`définition réservée à la phase ${PHASE_DEFINITION[d.type]}`);
            return;
        }
        let raison = null;
        let genreSource = null;
        for (const r of referencesDe(d)) {
            const cite = typeof r.id === 'string' ? r.id : String(r.id);
            const cible = typeof r.id === 'string' ? objets.get(r.id) : undefined;
            if (cible === undefined) {
                const place = places.get(cite);
                griefs.push(place === rang
                    ? `${quoi} (${id}) : « ${r.role} » se cite lui-même`
                    : place !== undefined && place > rang
                        ? `${quoi} (${id}) : « ${r.role} » cite « ${cite} », défini après lui`
                        : `${quoi} (${id}) : « ${r.role} » cite « ${cite} », absent`);
                raison ??= `« ${cite} » absent`;
                continue;
            }
            const genre = genres.get(cite);
            const attendus = genresAttendus(d.type, r.role);
            if (genre !== undefined && attendus !== null && !attendus.includes(genre)) {
                griefs.push(`${quoi} (${id}) : « ${r.role} » attend ${attendus.map((g) => LIBELLE_GENRE[g]).join(' ou ')}, « ${cite} » est ${LIBELLE_GENRE[genre]}`);
                raison ??= `« ${cite} » n'est pas du bon genre`;
                continue;
            }
            if (r.role === 'source')
                genreSource = genre ?? null;
            if (cible.genre === 'indefini')
                raison ??= `dépend de « ${cite} », sans solution`;
        }
        const genre = GENRE_PAR_TYPE[d.type];
        if (genre === 'image') {
            if (genreSource !== null)
                genres.set(id, genreSource);
        }
        else
            genres.set(id, genre);
        if (raison !== null) {
            indefini(raison);
            return;
        }
        const corps = calculer(d, (x) => objets.get(x));
        if ('raison' in corps)
            indefini(corps.raison);
        else if (!corpsFini(corps))
            indefini('calcul sans valeur finie');
        else
            objets.set(id, { ...tete, ...corps });
    });
    return { objets, griefs, repere, enonce: plan, unite };
}
/* ======================================================== le contrôle de forme */
const LATEX = /\\[([]|\$/;
/**
 * Les défauts de FORME d'un document (§ 4.2) : bornes, référence absente ou
 * postérieure, genre d'une référence, définition réservée à une phase non
 * livrée, repère illisible. Vide : le document peut être enregistré. Une
 * construction fausse n'est pas un grief — elle est enregistrée.
 *
 * `enonce` : la figure de l'énoncé NORMALISÉE, relue par le serveur, ou `null`.
 */
export function griefsDocument(doc, enonce, options = {}) {
    const phase = options.phase ?? PHASE_LIVREE;
    if (!estObjet(doc))
        return ["document : ce n'est pas un objet"];
    const g = [];
    const d = doc;
    if (d.version !== VERSION_CONSTRUCTION)
        g.push(`document : version ${String(d.version)} inconnue`);
    if (d.espace === 'espace')
        g.push("document : l'espace est réservé à la phase 4");
    else if (d.espace !== 'plan')
        g.push(`document : espace « ${String(d.espace)} » ni « plan » ni « espace »`);
    if (d.espaceTravail !== undefined)
        g.push('document : « espaceTravail » est réservé à la phase 4');
    if (d.statut !== 'brouillon' && d.statut !== 'reponse') {
        g.push(`document : statut « ${String(d.statut)} » ni « brouillon » ni « reponse »`);
    }
    if (d.unite !== null &&
        (typeof d.unite !== 'string' || d.unite.trim() === '' || [...d.unite].length > UNITE_MAX)) {
        g.push(`document : unité absente ou de plus de ${UNITE_MAX} caractères (null : nombres seuls)`);
    }
    const c = d.calque;
    if (!estObjet(c))
        g.push('document : calque absent');
    else {
        if (c.source !== null && c.source !== 'defi' && c.source !== 'partie' && c.source !== 'copie') {
            g.push(`calque : source « ${String(c.source)} » inconnue`);
        }
        if (typeof c.visible !== 'boolean')
            g.push('calque : « visible » doit valoir true ou false');
        if (c.exercice !== undefined) {
            if (c.source !== 'copie')
                g.push("calque : « exercice » n'a de sens que pour une copie");
            else if (typeof c.exercice !== 'string' ||
                c.exercice === '' ||
                [...c.exercice].length > EXERCICE_MAX) {
                g.push(`calque : exercice absent ou de plus de ${EXERCICE_MAX} caractères`);
            }
        }
    }
    if (!Array.isArray(d.objets))
        return [...g, 'document : « objets » doit être une liste'];
    if (d.objets.length > OBJETS_MAX_CONSTRUCTION) {
        g.push(`document : ${d.objets.length} objets, ${OBJETS_MAX_CONSTRUCTION} au plus`);
    }
    const plan = enonce !== null && enonce.espace === 'plan' ? enonce : null;
    const deLEnonce = new Set(plan === null ? [] : idsEnonce(plan));
    d.objets.forEach((o, i) => {
        if (!estObjet(o))
            return; // dit par la résolution
        const quoi = `objet ${i + 1}${typeof o.id === 'string' ? ` (${o.id})` : ''}`;
        if (o.nom !== undefined) {
            if (typeof o.nom !== 'string' ||
                o.nom.trim() === '' ||
                [...o.nom.trim()].length > NOM_MAX_CONSTRUCTION) {
                g.push(`${quoi} : nom vide ou de plus de ${NOM_MAX_CONSTRUCTION} caractères`);
            }
        }
        if (o.style !== undefined && o.style !== 'plein' && o.style !== 'pointille') {
            g.push(`${quoi} : style « ${String(o.style)} » ni « plein » ni « pointille »`);
        }
        if (o.masque !== undefined && typeof o.masque !== 'boolean') {
            g.push(`${quoi} : « masque » doit valoir true ou false`);
        }
        if (o.copieDe !== undefined && (typeof o.copieDe !== 'string' || !deLEnonce.has(o.copieDe))) {
            g.push(`${quoi} : copié de « ${String(o.copieDe)} », absent de l'énoncé`);
        }
        const def = o.def;
        if (estObjet(def) && estTypeDefinition(def.type) && PHASE_DEFINITION[def.type] <= phase) {
            g.push(...griefsBornes(def, quoi));
        }
    });
    const r = resoudreConstruction(doc, enonce, options);
    g.push(...r.griefs);
    for (const o of r.objets.values()) {
        const { id: _id, nom: _nom, style: _style, masque: _masque, ...corps } = o;
        if (!corpsFini(corps))
            g.push(`objet ${o.id} : résolution sans valeur finie`);
    }
    return g;
}
/** Les deux bouts d'une courbe bornée (tranche T42) ; `null` : pas deux valeurs de `BOUTS_V2`. */
function boutsLus(v) {
    if (!Array.isArray(v) || v.length !== 2)
        return null;
    const g = BOUTS_V2.find((b) => b === v[0]);
    const d = BOUTS_V2.find((b) => b === v[1]);
    return g === undefined || d === undefined ? null : [g, d];
}
/** Les bornes des champs d'une définition livrée (P1, P2). */
function griefsBornes(def, quoi) {
    const g = [];
    const nombre = (cle, positif = false) => {
        const v = def[cle];
        if (typeof v !== 'number' || !Number.isFinite(v) || Math.abs(v) > BORNE_CONSTRUCTION) {
            g.push(`${quoi} : « ${cle} » n'est pas un nombre fini dans ±${BORNE_CONSTRUCTION}`);
        }
        else if (positif && !(v > 0))
            g.push(`${quoi} : « ${cle} » doit être positif`);
    };
    const reference = (cle) => {
        if (typeof def[cle] !== 'string')
            g.push(`${quoi} : « ${cle} » doit citer un objet`);
    };
    switch (def.type) {
        case 'point_libre':
            nombre('x');
            nombre('y');
            break;
        case 'texte': {
            nombre('x');
            nombre('y');
            const t = def.texte;
            if (typeof t !== 'string' || t.trim() === '')
                g.push(`${quoi} : texte vide`);
            else {
                if ([...t].length > TEXTE_MAX_CONSTRUCTION) {
                    g.push(`${quoi} : texte de plus de ${TEXTE_MAX_CONSTRUCTION} caractères`);
                }
                if (LATEX.test(t))
                    g.push(`${quoi} : le texte d'une figure n'est pas du LaTeX`);
            }
            break;
        }
        case 'cercle_rayon':
            reference('centre');
            nombre('rayon', true);
            break;
        case 'polygone': {
            const s = def.sommets;
            if (!Array.isArray(s) || s.length < 3 || s.length > OBJETS_MAX_CONSTRUCTION) {
                g.push(`${quoi} : il faut de 3 à ${OBJETS_MAX_CONSTRUCTION} sommets`);
            }
            else if (!s.every((x) => typeof x === 'string')) {
                g.push(`${quoi} : chaque sommet doit citer un point`);
            }
            break;
        }
        case 'angle':
            reference('sommet');
            reference('de');
            reference('a');
            if (def.droit !== undefined && typeof def.droit !== 'boolean') {
                g.push(`${quoi} : « droit » doit valoir true ou false`);
            }
            break;
        case 'mesure_angle':
            reference('sommet');
            reference('de');
            reference('a');
            break;
        /* Le milieu, la médiatrice et la translation (P2) citent deux points,
         * comme le segment et la droite. */
        case 'segment':
        case 'droite':
        case 'mesure_longueur':
        case 'milieu':
        case 'mediatrice':
        case 'translation':
            reference('de');
            reference('a');
            break;
        case 'cercle_par_point':
            reference('centre');
            reference('par');
            break;
        /* ────────────────────────────── P2 ──────────────────────────────── */
        case 'point_sur':
            reference('objet');
            nombre('t');
            break;
        case 'intersection':
            reference('de');
            reference('a');
            if (def.branche !== 0 && def.branche !== 1) {
                g.push(`${quoi} : « branche » doit valoir 0 ou 1`);
            }
            break;
        case 'demi_droite':
            reference('origine');
            reference('par');
            break;
        case 'perpendiculaire':
        case 'parallele':
            reference('a');
            reference('par');
            break;
        case 'bissectrice':
            reference('sommet');
            reference('de');
            reference('a');
            break;
        case 'cercle_report':
            reference('centre');
            reference('longueurDe');
            break;
        case 'rotation':
            reference('centre');
            nombre('angle');
            break;
        case 'homothetie':
            reference('centre');
            nombre('rapport');
            /* Le rapport 0 écraserait la figure sur le centre : ce n'est pas une
             * homothétie. Le programme s'en tient d'ailleurs aux rapports positifs. */
            if (def.rapport === 0)
                g.push(`${quoi} : « rapport » ne peut pas être nul`);
            break;
        case 'reflexion':
            reference('axe');
            break;
        case 'image':
            reference('source');
            reference('par');
            break;
        /* ────────────────────────────── P3 ──────────────────────────────── */
        case 'fonction': {
            const famille = familleFonctionV2(def.famille);
            if (famille === null) {
                g.push(`${quoi} : famille « ${String(def.famille)} » inconnue`);
                break;
            }
            const r = fonctionCanoniqueV2(famille, def.parametres, def.domaine);
            if ('griefs' in r)
                for (const q of r.griefs)
                    g.push(`${quoi} (${famille}) : ${q}`);
            /* Tranche T42 : les bouts d'une courbe bornée — jamais sans domaine. */
            if (def.bouts !== undefined) {
                if (boutsLus(def.bouts) === null) {
                    g.push(`${quoi} (${famille}) : « bouts » : deux valeurs parmi ${BOUTS_V2.join(', ')}`);
                }
                else if (def.domaine === undefined) {
                    g.push(`${quoi} (${famille}) : « bouts » sans « domaine » — seule une courbe bornée a des bouts`);
                }
            }
            break;
        }
        case 'fonction_par_points': {
            const pts = def.points;
            if (!Array.isArray(pts) || pts.length !== 2 || !pts.every((x) => typeof x === 'string')) {
                g.push(`${quoi} : « points » doit citer deux points`);
            }
            /* Seule une droite se trace par deux points : deux points ne déterminent
             * ni une parabole ni une exponentielle. */
            if (def.famille !== 'lineaire') {
                g.push(`${quoi} : seule la famille « lineaire » se trace par deux points`);
            }
            break;
        }
        case 'vecteur':
            reference('de');
            reference('a');
            break;
        case 'demi_plan':
            reference('frontiere');
            if (!COTES_DEMI_PLAN_V2.includes(def.cote)) {
                g.push(`${quoi} : côté « ${String(def.cote)} » inconnu (${COTES_DEMI_PLAN_V2.join(', ')})`);
            }
            if (typeof def.stricte !== 'boolean') {
                g.push(`${quoi} : « stricte » doit valoir true ou false`);
            }
            break;
        case 'esquisse': {
            /* L6 (F8) : un ou deux traits, chacun de 2 à 200 points finis dans la borne. */
            const ts = def.traits;
            if (!Array.isArray(ts) || ts.length === 0 || ts.length > 2) {
                g.push(`${quoi} : une esquisse a un ou deux traits`);
                break;
            }
            for (const t of ts) {
                if (!Array.isArray(t) ||
                    t.length < 2 ||
                    t.length > 200 ||
                    !t.every((p) => typeof p === 'object' &&
                        p !== null &&
                        typeof p.x === 'number' &&
                        typeof p.y === 'number' &&
                        Number.isFinite(p.x) &&
                        Number.isFinite(p.y) &&
                        Math.abs(p.x) <= BORNE_CONSTRUCTION &&
                        Math.abs(p.y) <= BORNE_CONSTRUCTION)) {
                    g.push(`${quoi} : chaque trait a de 2 à 200 points finis, dans ±${BORNE_CONSTRUCTION}`);
                }
            }
            break;
        }
        case 'table_valeurs': {
            reference('fonction');
            const xs = def.x;
            if (!Array.isArray(xs) || xs.length === 0 || xs.length > TABLE_VALEURS_MAX) {
                g.push(`${quoi} : il faut de 1 à ${TABLE_VALEURS_MAX} abscisses`);
            }
            else if (!xs.every((v) => typeof v === 'number' && Number.isFinite(v) && Math.abs(v) <= BORNE_CONSTRUCTION)) {
                g.push(`${quoi} : chaque abscisse doit être un nombre fini dans ±${BORNE_CONSTRUCTION}`);
            }
            break;
        }
    }
    return g;
}
/** Le texte d'une mesure : « 4,12 cm », « 37° ». Calculé, jamais saisi. */
export function texteMesure(sorte, valeur, unite) {
    return sorte === 'angle'
        ? `${formaterNombre(valeur)}°`
        : `${formaterNombre(valeur)}${unite === null ? '' : ` ${unite}`}`;
}
/**
 * Un objet résolu de l'élève en objets du moteur ; liste vide : rien à
 * dessiner. Une LISTE et non un objet : une table de valeurs pose autant de
 * marques qu'elle a d'abscisses (P3), et tous portent le même identifiant —
 * c'est un seul objet de l'atelier, qu'un seul toucher sélectionne.
 */
function versObjetPlan(o, r) {
    switch (o.genre) {
        case 'point':
            return [{ type: 'point', nom: o.nom ?? '', x: o.p.x, y: o.p.y }];
        case 'ligne': {
            const style = o.style ?? 'plein';
            if (o.sorte === 'demi_droite') {
                const c = couperDemiDroite(o.a, o.b, r.repere);
                return c === null ? [] : [{ type: 'segment', de: c[0], a: c[1], style }];
            }
            return [{ type: o.sorte, de: o.a, a: o.b, style }];
        }
        case 'cercle':
            return [{ type: 'cercle', centre: o.centre, rayon: o.rayon }];
        case 'polygone':
            return o.sommets.length >= 3 ? [{ type: 'polygone', sommets: o.sommets }] : [];
        case 'fonction': {
            const courbe = { type: 'fonction', fonction: o.f, domaine: o.domaine };
            if (o.bouts === undefined || o.domaine === null)
                return [courbe];
            /* Tranche T42 : ses bouts, un point plein ou vide chacun — des BORNES, sans nom. */
            const bornes = [];
            o.bouts.forEach((bout, i) => {
                const x = o.domaine?.[i];
                if (bout === 'aucun' || x === undefined)
                    return;
                const y = evaluerFonctionV2(o.f, x);
                if (Number.isFinite(y))
                    bornes.push({ type: 'point', nom: '', x, y, creux: bout === 'vide' });
            });
            return [courbe, ...bornes];
        }
        case 'vecteur':
            return [{ type: 'vecteur', de: o.de, a: o.a }];
        case 'demi_plan':
            return [{ type: 'demi_plan', de: o.a, a: o.b, cote: o.cote, stricte: o.stricte }];
        case 'marque':
            if (o.sorte === 'angle') {
                return [
                    { type: 'angle', sommet: o.sommet, de: o.de, a: o.a, droit: o.droit, texte: o.texte },
                ];
            }
            if (o.sorte === 'cote')
                return [{ type: 'cote', de: o.de, a: o.a, texte: o.texte }];
            /* L6 (F8) : une esquisse se dessine trait par trait, en petits segments. */
            if (o.sorte === 'esquisse') {
                return o.traits.flatMap((t) => t.slice(1).flatMap((p, i) => {
                    const q = t[i];
                    return distance(p, q) < CONFONDUS
                        ? []
                        : [
                            {
                                type: 'segment',
                                de: { x: q.x, y: q.y },
                                a: { x: p.x, y: p.y },
                                style: 'plein',
                            },
                        ];
                }));
            }
            return o.points.map((p) => ({ type: 'point', nom: '', x: p.x, y: p.y }));
        case 'mesure': {
            const texte = texteMesure(o.sorte, o.valeur, r.unite);
            if (o.sorte === 'angle') {
                const [sommet, de, a] = o.points;
                return [{ type: 'angle', sommet, de, a, droit: false, texte }];
            }
            const [de, a] = o.points;
            return distance(de, a) < CONFONDUS ? [] : [{ type: 'cote', de, a, texte }];
        }
        case 'texte':
            return [{ type: 'etiquette', texte: o.texte, x: o.p.x, y: o.p.y }];
        default:
            // transformation (elle n'a pas de tracé propre), indéfini : rien à dessiner.
            return [];
    }
}
/**
 * La construction résolue en figure v2 du plan, prête pour `dessinerPlan`
 * (§ 1.4) : le calque de l'énoncé au ton `enonce` (ses objets identifiés par
 * `idsEnonce`), puis les objets de l'élève au ton `eleve`, dans l'ordre du
 * document. Un objet indéfini, masqué ou sans tracé propre n'est pas dessiné.
 * Chaque objet porte son identifiant : `dessinerPlan` le recopie en `source`.
 */
export function versFigureV2(resolue, options = {}) {
    const objets = [];
    if (options.calque !== false && resolue.enonce !== null) {
        const ids = idsEnonce(resolue.enonce);
        resolue.enonce.objets.forEach((o, i) => {
            objets.push({ ...o, id: ids[i], ton: 'enonce' });
        });
    }
    for (const o of resolue.objets.values()) {
        if (estIdEnonce(o.id))
            continue;
        if (o.masque === true && options.masques !== true)
            continue;
        for (const v of versObjetPlan(o, resolue))
            objets.push({ ...v, id: o.id, ton: 'eleve' });
    }
    return { espace: 'plan', taille: options.taille ?? 'pleine', repere: resolue.repere, objets };
}
/* ======================================================= partir de l'énoncé */
/**
 * « Partir de l'énoncé » (§ 1.3) : les objets de l'énoncé recopiés en objets
 * de l'élève, qu'il peut alors déplacer. Chaque copie porte `copieDe`.
 *
 *  - chaque point devient un `point_libre` de même nom ;
 *  - un segment, une droite, un polygone, un cercle, une marque d'angle
 *    citent ces points (une extrémité écrite par ses seules coordonnées
 *    devient un point sans nom) ; une étiquette devient un texte ;
 *  - une cote (une donnée, pas une construction) et une courbe de fonction
 *    (phase 3) ne sont pas recopiées.
 *
 * Les identifiants sont numérotés à partir de `premierNumero` (« p7 »,
 * « d8 »…) : l'appelant le tire de son historique (`numeroLibre`), jamais du
 * hasard.
 */
export function copierEnonce(enonce, premierNumero) {
    let n = premierNumero;
    const ids = idsEnonce(enonce);
    const sortie = [];
    const cle = (p) => `${p.x};${p.y}`;
    const copies = new Map();
    enonce.objets.forEach((o, i) => {
        if (o.type !== 'point' || copies.has(cle(o)))
            return;
        const id = `p${n++}`;
        sortie.push({ id, nom: o.nom, copieDe: ids[i], def: { type: 'point_libre', x: o.x, y: o.y } });
        copies.set(cle(o), id);
    });
    const pointDe = (p) => {
        const deja = copies.get(cle(p));
        if (deja !== undefined)
            return deja;
        const id = `p${n++}`;
        sortie.push({ id, def: { type: 'point_libre', x: p.x, y: p.y } });
        copies.set(cle(p), id);
        return id;
    };
    enonce.objets.forEach((o, i) => {
        const copieDe = ids[i];
        switch (o.type) {
            case 'segment':
            case 'droite': {
                const de = pointDe(o.de);
                const a = pointDe(o.a);
                sortie.push({
                    id: `d${n++}`,
                    copieDe,
                    ...(o.style === 'pointille' ? { style: 'pointille' } : {}),
                    def: { type: o.type, de, a },
                });
                break;
            }
            case 'polygone': {
                const sommets = o.sommets.map(pointDe);
                sortie.push({ id: `g${n++}`, copieDe, def: { type: 'polygone', sommets } });
                break;
            }
            case 'cercle': {
                const centre = pointDe(o.centre);
                sortie.push({
                    id: `c${n++}`,
                    copieDe,
                    def: { type: 'cercle_rayon', centre, rayon: o.rayon },
                });
                break;
            }
            case 'angle': {
                const sommet = pointDe(o.sommet);
                const de = pointDe(o.de);
                const a = pointDe(o.a);
                sortie.push({
                    id: `m${n++}`,
                    copieDe,
                    def: { type: 'angle', sommet, de, a, ...(o.droit ? { droit: true } : {}) },
                });
                break;
            }
            case 'etiquette':
                sortie.push({
                    id: `t${n++}`,
                    copieDe,
                    def: { type: 'texte', x: o.x, y: o.y, texte: o.texte },
                });
                break;
            default:
                break;
        }
    });
    return sortie;
}
//# sourceMappingURL=construction.js.map